import { Injectable } from '@angular/core';
import { ConnectionStatusService } from '../connection-status.service';
import { RequestService } from '../request.service';
import * as semaphore from 'semaphore';
import SemaphoreHelper from 'src/app/helpers/semaphore-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { OfflineProjectStorageService } from '../offline-project-storage.service';
import {
  OfflineProductChangeObject,
  PlacerProductChangesService,
} from './placer-product-changes.service';
import {
  OfflineSubProjectChangeObject,
  PlacerSubProjectChangesService,
} from './placer-subproject-changes.service';
import { OfflineAssetSubmitService } from './offine-asset-submit.service';
import RegisteredActionType from 'src/app/model/registered-action-type';
import { Project } from 'src/app/model/project/project';
import ErrorHelper from 'src/app/helpers/error-helper';
import { SnackService } from '../snack.service';
import { OfflineAsset } from './offline-asset';
import ProductAsset from 'src/app/model/product-asset';
import { interval, merge, Observable, Subject } from 'rxjs';
import { StringHelper } from 'src/app/helpers/string-helper';
import RegisteredAction from 'src/app/model/registered-action';
import {
  SubmitEventService,
  SubmitInProgressType,
} from './submit-event.service';

@Injectable({
  providedIn: 'root',
})
export class PlacerSubmitService {
  private isSubmitting: boolean = false;
  private online: boolean = true;
  private readonly toSubmitLocalStorageKey = 'toSubmit';
  private readonly toSubmitGetSemaphore = semaphore(1);
  private readonly trySubmitInterval = 1000 * 10; // 10 seconds
  private connectionRestoredSubject = new Subject<any>();
  private registeredActionsSubmittedForSubProjectSubject =
    new Subject<SubmittedRegisteredActions>();
  private readonly toSubmitChange = new Subject<any>();

  constructor(
    private requestService: RequestService,
    private connectionStatusService: ConnectionStatusService,
    private offlineProjectStorageService: OfflineProjectStorageService,
    private placerProductChangesService: PlacerProductChangesService,
    private placerSubProjectChangesService: PlacerSubProjectChangesService,
    private offlineAssetSubmitService: OfflineAssetSubmitService,
    private submitEventService: SubmitEventService,
    private snackService: SnackService
  ) {
    this.connectionStatusService
      .getConnectionStatusSubject()
      .subscribe((online: boolean) => {
        const doSubmit = !this.online && online;
        this.online = online;
        if (doSubmit) {
          this.connectionRestoredSubject.next();
        }
      });
    merge(
      this.connectionRestoredSubject,
      interval(this.trySubmitInterval)
    ).subscribe(() => {
      this.doSubmit();
    });
  }

  public submitProject(
    subProjectChanges: OfflineSubProjectChangeObject
  ): Promise<Project> {
    if (this.online) {
      return this.doSubmitProject(subProjectChanges, true).catch((err) => {
        if (ErrorHelper.isConnectionLostError(err)) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, project zal ingediend worden zodra internetverbinding hersteld is'
          );
          this.addToSubmit(subProjectChanges.id, SubmitType.Full);
        }
        return Promise.reject(err);
      });
    } else {
      this.snackService.showWarning(
        'Server kon niet bereikt worden, project zal ingediend worden zodra internetverbinding hersteld is'
      );
      this.addToSubmit(subProjectChanges.id, SubmitType.Full);
      return Promise.reject({ status: 0 });
    }
  }

  public submitRegisteredActions(
    subProjectChanges: OfflineSubProjectChangeObject
  ): Promise<RegisteredAction[]> {
    if (this.online) {
      return this.doSubmitRegisteredActions(subProjectChanges, true).catch(
        (err) => {
          if (ErrorHelper.isConnectionLostError(err)) {
            this.addToSubmit(
              subProjectChanges.id,
              SubmitType.RegisteredActions
            );
          }
          return Promise.reject(err);
        }
      );
    } else {
      this.addToSubmit(subProjectChanges.id, SubmitType.RegisteredActions);
      return Promise.reject({ status: 0 });
    }
  }

  private async doSubmit(): Promise<void> {
    if (this.isSubmitting || !this.online) {
      return;
    }
    this.isSubmitting = true;
    try {
      const toSubmit = await this.getToSubmit();
      if (!CollectionHelper.isPopulated(toSubmit)) {
        return;
      }
      for (const submitEntry of toSubmit) {
        const subProjectChanges =
          this.placerSubProjectChangesService.getDirectOfflineSubProjectChangeObject(
            Number(submitEntry.subProjectId)
          );
        const isInProgress = await this.submitEventService.isInProgress(
          submitEntry.subProjectId,
          SubmitInProgressType.SUB_PROJECT
        );
        if (isInProgress) {
          continue;
        } else if (subProjectChanges == null) {
          await this.removeToSubmit(submitEntry.subProjectId);
        } else {
          try {
            submitEntry.submitType === SubmitType.Full
              ? await this.doSubmitProject(subProjectChanges, false)
              : await this.doSubmitRegisteredActions(subProjectChanges, false);
          } catch (err) {
            if (!ErrorHelper.isConnectionLostError(err)) {
              this.snackService.showError(
                `Fout bij indienen subproject ${submitEntry.subProjectId}: ${err}`
              );
            }
          }
        }
      }
    } catch (err) {
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  }

  public async retrySubmit(subProjectId: number): Promise<void> {
    if (!this.online) {
      this.snackService.showWarning(
        'Er is geen verbinding, gelieve opnieuw te proberen als de verbinding hersteld is'
      );
      return;
    }
    const isInProgress = await this.submitEventService.isInProgress(
      subProjectId.toString(),
      SubmitInProgressType.SUB_PROJECT
    );
    if (isInProgress) {
      return;
    }
    const subProjectChanges =
      this.placerSubProjectChangesService.getDirectOfflineSubProjectChangeObject(
        subProjectId
      );
    if (subProjectChanges == null) {
      await this.removeToSubmit(subProjectId.toString());
    } else {
      try {
        await this.doSubmitProject(subProjectChanges, false);
      } catch (err) {
        if (!ErrorHelper.isConnectionLostError(err)) {
          this.snackService.showError(
            `Fout bij indienen subproject ${subProjectId}: ${err}`
          );
        }
      }
    }
  }

  private async doSubmitProject(
    subProjectChanges: OfflineSubProjectChangeObject,
    immediateSubmit = false
  ): Promise<Project> {
    if (subProjectChanges.projectId == null) {
      await this.removeToSubmit(subProjectChanges.id.toString());
      return;
    }
    const project = this.offlineProjectStorageService.getStoredProject(
      subProjectChanges.projectId
    );
    if (project == null || !CollectionHelper.isPopulated(project.subProjects)) {
      await this.removeToSubmit(subProjectChanges.id.toString());
      return;
    }
    const storedSubProject = project.subProjects.find(
      (sp) => sp.id === subProjectChanges.id
    );
    if (storedSubProject == null) {
      await this.removeToSubmit(subProjectChanges.id.toString());
      return;
    }
    let productChangeObjects: OfflineProductChangeObject[];
    if (storedSubProject.type === 'BaseProject') {
      if (!CollectionHelper.isPopulated(project.products)) {
        await this.removeToSubmit(subProjectChanges.id.toString());
        return;
      }
      productChangeObjects =
        this.placerProductChangesService.getDirectOfflineProductChangeObjects(
          project.products.map((p) => p.id)
        );
    }
    await this.submitEventService.setSubmitInProgress(
      subProjectChanges.id.toString(),
      SubmitInProgressType.SUB_PROJECT
    );
    try {
      await this.submitOfflineAssets(
        subProjectChanges,
        productChangeObjects,
        immediateSubmit
      );
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err) && !immediateSubmit) {
        this.snackService.showError(err);
        await this.removeLocalChanges(subProjectChanges, productChangeObjects);
      } else {
        if (ErrorHelper.isConnectionLostError(err) && immediateSubmit) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, project zal ingediend worden zodra internetverbinding hersteld is'
          );
        } else if (immediateSubmit) {
          this.snackService.showError(err);
        }
      }
      await this.submitEventService.removeSubmitInProgress(
        subProjectChanges.id.toString(),
        SubmitInProgressType.SUB_PROJECT
      );
      throw err;
    }
    const requestBody = this.constructRequestBody(
      subProjectChanges,
      productChangeObjects
    );
    return new Promise((resolve, reject) => {
      this.requestService
        .post(requestBody, '/projects/installerSubmit/', null, false)
        .subscribe(
          (project) => {
            this.offlineProjectStorageService.storeProject(project);
            this.removeLocalChanges(
              subProjectChanges,
              productChangeObjects
            ).then(() => resolve(project));
            this.submitEventService.removeSubmitInProgress(
              subProjectChanges.id.toString(),
              SubmitInProgressType.SUB_PROJECT
            );
          },
          (err) => {
            if (!ErrorHelper.isConnectionLostError(err) && !immediateSubmit) {
              this.snackService.showError(err);
              this.removeLocalChanges(
                subProjectChanges,
                productChangeObjects
              ).then(() => reject(err));
            } else {
              if (ErrorHelper.isConnectionLostError(err) && immediateSubmit) {
                this.snackService.showWarning(
                  'Server kon niet bereikt worden, project zal ingediend worden zodra internetverbinding hersteld is'
                );
              } else if (immediateSubmit) {
                this.snackService.showError(err);
              }
              reject(err);
            }
            this.submitEventService.removeSubmitInProgress(
              subProjectChanges.id.toString(),
              SubmitInProgressType.SUB_PROJECT
            );
          }
        );
    });
  }

  private async addToSubmit(id: number, submitType: SubmitType): Promise<void> {
    await SemaphoreHelper.use(this.toSubmitGetSemaphore, async () => {
      const toAdd = id.toString();
      const stringified = localStorage.getItem(this.toSubmitLocalStorageKey);
      const toSave: SubmitEntry[] =
        stringified == null ? [] : JSON.parse(stringified);
      const earlierItem = toSave.find((i) => i.subProjectId === toAdd);
      if (earlierItem == null) {
        toSave.push({
          subProjectId: toAdd,
          submitType: submitType,
        });
      } else if (earlierItem.submitType === SubmitType.RegisteredActions) {
        earlierItem.submitType = submitType;
      }
      localStorage.setItem(
        this.toSubmitLocalStorageKey,
        JSON.stringify(toSave)
      );
      this.toSubmitChange.next();
    });
  }

  public async getToSubmit(): Promise<SubmitEntry[]> {
    let toReturn: SubmitEntry[];
    await SemaphoreHelper.use(this.toSubmitGetSemaphore, async () => {
      const stringified = localStorage.getItem(this.toSubmitLocalStorageKey);
      if (stringified == null) {
        return;
      }
      toReturn = <SubmitEntry[]>JSON.parse(stringified);
    });
    return toReturn;
  }

  private async removeToSubmit(id: string) {
    if (StringHelper.isEmpty(id)) {
      return;
    }
    await SemaphoreHelper.use(this.toSubmitGetSemaphore, async () => {
      const stringified = localStorage.getItem(this.toSubmitLocalStorageKey);
      if (stringified == null) {
        return;
      }
      const list = <SubmitEntry[]>JSON.parse(stringified);
      CollectionHelper.filterInPlace(
        list,
        (e) => e.subProjectId != null && e.subProjectId !== id
      );
      localStorage.setItem(this.toSubmitLocalStorageKey, JSON.stringify(list));
      this.toSubmitChange.next();
    });
  }

  private constructRequestBody(
    subProjectChanges: OfflineSubProjectChangeObject,
    productChangeObjects?: OfflineProductChangeObject[]
  ): SubmitRequestBody {
    const registeredActionBodies: SubmitRequestRegisteredAction[] =
      CollectionHelper.isPopulated(subProjectChanges.registeredActions)
        ? subProjectChanges.registeredActions.map((r) => {
            return {
              id: r.id,
              user: r.user,
              date: r.date,
              type: r.type,
            };
          })
        : null;
    const subProjectBody: SubmitRequestSubProject = {
      id: subProjectChanges.id,
      placerFeedback: subProjectChanges.placerFeedback,
      registeredActions: registeredActionBodies,
      placementDone: subProjectChanges.placementDone,
      currentExecutionDate: subProjectChanges.currentExecutionDate,
    };
    const requestBody: SubmitRequestBody = {
      projectId: subProjectChanges.projectId,
      subProject: subProjectBody,
    };
    if (CollectionHelper.isPopulated(productChangeObjects)) {
      requestBody.products = productChangeObjects.map((p) => {
        return {
          id: p.id,
          extraInfoVisibleDamage: p.extraInfoVisibleDamage,
          installerFeedback: p.installerFeedback,
          executionComplete: p.executionComplete,
          executionDate: p.executionDate,
          executionTime: p.executionTime,
        };
      });
    }
    return requestBody;
  }

  private async removeLocalChanges(
    subProjectChanges: OfflineSubProjectChangeObject,
    productChangeObjects?: OfflineProductChangeObject[]
  ) {
    await this.removeToSubmit(subProjectChanges.id.toString());
    this.placerSubProjectChangesService.removeOfflineSubProjectChangeObject(
      subProjectChanges
    );
    this.placerProductChangesService.removeOfflineProductChangeObjects(
      productChangeObjects
    );
  }

  private submitOfflineAssets(
    subProjectChanges: OfflineSubProjectChangeObject,
    productChangeObjects?: OfflineProductChangeObject[],
    immediateSubmit = false
  ): Promise<void> {
    return productChangeObjects != null
      ? this.submitOfflineProductAssets(productChangeObjects, immediateSubmit)
      : this.submitOfflineProjectPlacementAssets(
          subProjectChanges,
          immediateSubmit
        );
  }

  private async submitOfflineProductAssets(
    productChangeObjects: OfflineProductChangeObject[],
    immediateSubmit: boolean
  ) {
    if (productChangeObjects.length === 0) {
      return;
    }
    for (const productChangeObject of productChangeObjects) {
      if (!CollectionHelper.isPopulated(productChangeObject.offlineAssets)) {
        continue;
      }
      const unsubmittedAssets = this.filterUnsubmittedAssets(
        productChangeObject.offlineAssets
      );
      if (!CollectionHelper.isPopulated(unsubmittedAssets)) {
        continue;
      }
      try {
        await this.submitMultipleOfflineAssets(
          unsubmittedAssets,
          productChangeObject,
          null,
          this.handleProductAssetResultMapBound,
          immediateSubmit
        );
      } catch (err) {
        throw err;
      }
    }
  }

  private async submitOfflineProjectPlacementAssets(
    subProjectChanges: OfflineSubProjectChangeObject,
    immediateSubmit: boolean
  ) {
    if (!CollectionHelper.isPopulated(subProjectChanges.offlineAssets)) {
      return;
    }
    const unsubmittedAssets = this.filterUnsubmittedAssets(
      subProjectChanges.offlineAssets
    );
    if (!CollectionHelper.isPopulated(unsubmittedAssets)) {
      return;
    }
    try {
      await this.submitMultipleOfflineAssets(
        unsubmittedAssets,
        null,
        subProjectChanges,
        this.handleProjectPlacementResultMapBound,
        immediateSubmit
      );
    } catch (err) {
      throw err;
    }
  }

  private filterUnsubmittedAssets(offlineAssets: OfflineAsset[]) {
    return offlineAssets == null
      ? null
      : offlineAssets.filter((a) => a.uuid == null);
  }

  private async submitMultipleOfflineAssets(
    offlineAssets: OfflineAsset[],
    productChangeObject: OfflineProductChangeObject | null,
    subProjectChangeObject: OfflineSubProjectChangeObject | null,
    resultMapHandler: (
      m: Map<string, ProductAsset>,
      productChangeObject: OfflineProductChangeObject | null,
      subProjectChangeObject: OfflineSubProjectChangeObject | null
    ) => Promise<void>,
    immediateSubmit: boolean
  ): Promise<void> {
    const submitResult =
      await this.offlineAssetSubmitService.submitMultipleOfflineAssets(
        offlineAssets,
        productChangeObject != null ? productChangeObject.id : null,
        subProjectChangeObject != null ? subProjectChangeObject.id : null,
        immediateSubmit
      );
    if (submitResult.resultMap.size > 0) {
      await resultMapHandler(
        submitResult.resultMap,
        productChangeObject,
        subProjectChangeObject
      );
    }
    if (submitResult.error != null) {
      if (immediateSubmit) {
        if (ErrorHelper.isConnectionLostError(submitResult.error)) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, project zal ingediend worden zodra internetverbinding hersteld is'
          );
        } else {
          this.snackService.showError(submitResult.error);
        }
      }
      throw submitResult.error;
    }
  }

  private async handleProductAssetResultMap(
    resultMap: Map<string, ProductAsset>,
    productChangeObject: OfflineProductChangeObject,
    subProjectChangeObject: OfflineSubProjectChangeObject
  ): Promise<void> {
    productChangeObject.offlineAssets.forEach((o) => {
      const savedAsset = o.uuid == null ? resultMap.get(o.localFileUuid) : null;
      if (savedAsset != null) {
        o.uuid = savedAsset.asset.uuid;
      }
    });
    await this.placerProductChangesService.storeOfflineProductChangeObject(
      productChangeObject
    );
  }

  private handleProductAssetResultMapBound =
    this.handleProductAssetResultMap.bind(this);

  private async handleProjectPlacementResultMap(
    resultMap: Map<string, ProductAsset>,
    productChangeObject: OfflineProductChangeObject,
    subProjectChangeObject: OfflineSubProjectChangeObject
  ): Promise<void> {
    subProjectChangeObject.offlineAssets.forEach((o) => {
      const savedAsset = o.uuid == null ? resultMap.get(o.localFileUuid) : null;
      if (savedAsset != null) {
        o.uuid = savedAsset.asset.uuid;
      }
    });
    await this.placerSubProjectChangesService.storeOfflineSubProjectChangeObject(
      subProjectChangeObject
    );
  }

  private handleProjectPlacementResultMapBound =
    this.handleProjectPlacementResultMap.bind(this);

  private async doSubmitRegisteredActions(
    subProjectChanges: OfflineSubProjectChangeObject,
    immediateSubmit = false
  ): Promise<RegisteredAction[]> {
    if (!CollectionHelper.isPopulated(subProjectChanges.registeredActions)) {
      throw new Error('Er zijn geen lokale geregistreerde acties');
    }
    if (subProjectChanges.projectId == null) {
      await this.removeToSubmit(subProjectChanges.id.toString());
      return;
    }

    const requestBody: SubmitRegisteredActionsRequestBody = {
      subProjectId: subProjectChanges.id,
      registeredActions: [...subProjectChanges.registeredActions],
    };

    return new Promise((resolve, reject) => {
      this.requestService
        .post(requestBody, '/projects/registeredActionsSubmit', null, false)
        .subscribe(async (registeredActions: RegisteredAction[]) => {
          subProjectChanges.registeredActions.length = 0;
          await this.placerSubProjectChangesService.storeOfflineSubProjectChangeObject(
            subProjectChanges
          );
          if (CollectionHelper.isPopulated(registeredActions)) {
            await this.offlineProjectStorageService.storeRegisteredActions(
              subProjectChanges.projectId,
              subProjectChanges.id,
              registeredActions
            );
          }
          await this.removeToSubmit(subProjectChanges.id.toString());
          if (!immediateSubmit) {
            this.registeredActionsSubmittedForSubProjectSubject.next({
              subProjectId: subProjectChanges.id,
              registeredActions,
            });
          }
          resolve(registeredActions);
        }),
        (err) => {
          if (!ErrorHelper.isConnectionLostError(err) && !immediateSubmit) {
            this.snackService.showError(err);
            this.removeLocalChanges(subProjectChanges);
          }
          reject(err);
        };
    });
  }

  public getRegisteredActionsSubmittedForSubProjectObservable(): Observable<SubmittedRegisteredActions> {
    return this.registeredActionsSubmittedForSubProjectSubject;
  }

  public getToSubmitChangedSubscribable(): Subject<any> {
    return this.toSubmitChange;
  }
}

export enum SubmitType {
  Full,
  RegisteredActions,
}

export interface SubmitEntry {
  subProjectId: string;
  submitType: SubmitType;
}

interface SubmitRequestBody {
  projectId: number;
  products?: SubmitRequestProduct[];
  subProject: SubmitRequestSubProject;
}

interface SubmitRequestProduct {
  id: number;
  extraInfoVisibleDamage?: string;
  installerFeedback?: string;
  executionComplete?: boolean;
  executionDate?: Date;
  executionTime?: string;
}

interface SubmitRequestSubProject {
  id: number;
  placerFeedback?: string;
  registeredActions?: SubmitRequestRegisteredAction[];
  placementDone?: boolean;
  currentExecutionDate?: Date;
}

interface SubmitRequestRegisteredAction {
  id?: number;
  user?: SubmitRequestUser;
  date?: Date;
  type?: RegisteredActionType;
}

interface SubmitRequestUser {
  id?: number;
}

interface SubmitRegisteredActionsRequestBody {
  subProjectId: number;
  registeredActions: SubmitRequestRegisteredAction[];
}

export interface SubmittedRegisteredActions {
  subProjectId: number;
  registeredActions: RegisteredAction[];
}
